import {nodeToText} from '@frontapp/editor-html';
import {noop} from 'lodash';
import {RefObject, useCallback, useEffect} from 'react';

import {forEachElementInTree, isHtmlElement} from '../../../helpers/src/browser/domHelpers';

/*
 * Constants.
 */

const IDENTIFIER = 'data-dark-copy';

/*
 * Types.
 */

type ProcessNode = (original: HTMLElement, copy: HTMLElement) => void;

/*
 * Helpers.
 */

// Adds a data attribute identifier to all nodes within the DOM subtree.
// Allows us to identify the same node in a cloned tree.
function annotateNodes(root: HTMLElement) {
  let index = 1;
  forEachElementInTree(root, (node: HTMLElement) => {
    node.setAttribute(IDENTIFIER, String(index++));
  });
}

// Removes annotations added with annotateNodes.
function cleanupNodeAnnotations(root: HTMLElement) {
  forEachElementInTree(root, (node: HTMLElement) => {
    node.removeAttribute(IDENTIFIER);
  });
}

// Checks annotations to determine whether the cloned node came from
// the original node.
function isClonedNode(original: HTMLElement, copy: HTMLElement) {
  return (
    original.hasAttribute(IDENTIFIER) && original.getAttribute(IDENTIFIER) === copy.getAttribute(IDENTIFIER)
  );
}

function findNextNode(treeWalker: TreeWalker) {
  const nextNode = treeWalker.nextNode();
  if (!isHtmlElement(nextNode)) {
    return null;
  }
  return nextNode;
}

// Traverses the original and cloned trees. The goal of this function is to call
// the callback for every node in the cloned tree, along with the corresponding node
// from the original tree. The cloned tree is a subset of the original tree, so data
// attribute annotations are used to identify the matching nodes.
function traverseTrees(original: HTMLElement, copy: HTMLElement, callback: ProcessNode) {
  const document = original.ownerDocument;
  const originalTreeWalker = document.createTreeWalker(original, NodeFilter.SHOW_ELEMENT);
  const copyTreeWalker = document.createTreeWalker(copy, NodeFilter.SHOW_ELEMENT);

  let currentOriginalNode: HTMLElement | null = original;
  let currentCopyNode: HTMLElement | null = copy;

  while (currentOriginalNode && currentCopyNode) {
    // Since the original tree is a superset of the cloned tree, increment the original
    // if the nodes aren't matching.
    if (!isClonedNode(currentOriginalNode, currentCopyNode)) {
      currentOriginalNode = findNextNode(originalTreeWalker);
      continue;
    }

    callback(currentOriginalNode, currentCopyNode);
    currentOriginalNode = findNextNode(originalTreeWalker);
    currentCopyNode = findNextNode(copyTreeWalker);
  }
}

// Takes the document.getSelection(), copies the selected nodes into memory, and then runs the
// processNode function on each of them. Returns an innerHTML string.
function processSelectedNodes(document: Document, processNode: ProcessNode) {
  const selection = document.getSelection();
  if (!selection) {
    return null;
  }

  const range = selection.getRangeAt(0);

  // range.commonAncestorContainer can be a text node if the start and end bounds are within
  // a single node.
  const container = findClosestHtmlElement(range.commonAncestorContainer);
  if (!container) {
    return null;
  }

  annotateNodes(container);
  const copyContainer = container?.cloneNode() || null;
  const copyContent = range.cloneContents();

  if (!isHtmlElement(container) || !isHtmlElement(copyContainer)) {
    return null;
  }

  copyContainer.appendChild(copyContent);

  traverseTrees(container, copyContainer, processNode);

  cleanupNodeAnnotations(container);
  cleanupNodeAnnotations(copyContainer);

  const sentinel = document.createElement('div');
  sentinel.appendChild(copyContainer);
  return {
    html: sentinel.innerHTML,
    text: nodeToText(copyContainer),
  };
}

// Intercepts the provided copy event, processing the selected nodes with the processNode function.
// Sets the processed HTML on the user's clipboard.
function processSelectedNodesOnCopyEvent(event: ClipboardEvent, processNode: ProcessNode) {
  const container = event.currentTarget;
  if (!container || !isHtmlElement(container) || !event.clipboardData) {
    return;
  }

  const processedStrings = processSelectedNodes(container.ownerDocument, processNode);
  if (!processedStrings) {
    return;
  }

  event.clipboardData.setData('text/html', processedStrings.html);
  event.clipboardData.setData('text/plain', processedStrings.text);
  event.preventDefault();
}

/*
 * Hook.
 */

// Convenience hook for the processSelectedNodesOnCopyEvent function.
export function useProcessHtmlBeforeCopy(
  elementRef: RefObject<HTMLElement>,
  processNode: ProcessNode,
  isEnabled: boolean,
) {
  const onCopy = useCallback(
    (event: ClipboardEvent) => {
      processSelectedNodesOnCopyEvent(event, processNode);
    },
    [processNode],
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !isEnabled) {
      return noop;
    }

    element.addEventListener('copy', onCopy);
    return () => {
      element.removeEventListener('copy', onCopy);
    };
  }, [elementRef, onCopy, isEnabled]);
}

function findClosestHtmlElement(node: Node) {
  if (isHtmlElement(node)) {
    return node;
  }

  return node.parentElement;
}
